import { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  Image, 
  FileSpreadsheet,
  Play, 
  Trash2, 
  CheckCircle,
  AlertCircle,
  Loader2,
  FolderOpen,
  Palette,
  FolderInput,
  FolderOutput
} from 'lucide-react';
import FeatureGuard from '../components/FeatureGuard';

function DataMatchContent() {
  const [photoFolderHandle, setPhotoFolderHandle] = useState(null);
  const [logoFolderHandle, setLogoFolderHandle] = useState(null);
  const [outputFolderHandle, setOutputFolderHandle] = useState(null);
  const [excelFileHandle, setExcelFileHandle] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [results, setResults] = useState(null);
  
  // Ana klasör yolu - localStorage'dan yükle
  const [basePath, setBasePath] = useState(() => {
    return localStorage.getItem('dataMatch_basePath') || '';
  });

  // Ana yolu kaydet
  const handleBasePathChange = (value) => {
    setBasePath(value);
    localStorage.setItem('dataMatch_basePath', value);
  };

  const isSupported = 'showDirectoryPicker' in window;

  // Excel tarih sayısını JS Date'e çevir
  const excelDateToJSDate = (excelDate) => {
    if (typeof excelDate === 'number') {
      // Excel tarihleri 1900-01-01'den itibaren gün sayısı
      const date = new Date((excelDate - 25569) * 86400 * 1000);
      return date;
    }
    if (excelDate instanceof Date) {
      return excelDate;
    }
    if (typeof excelDate === 'string') {
      const parsed = new Date(excelDate);
      if (!isNaN(parsed)) return parsed;
    }
    return null;
  };

  // Tarihi dd.mm.yyyy formatına çevir
  const formatDate = (date) => {
    if (!date) return '';
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  const extractBarcodes = useCallback((files) => {
    const barcodes = new Map();
    files.forEach(file => {
      const match = file.name.match(/(\d{13})/);
      if (match) {
        const barcode = match[1];
        if (!barcodes.has(barcode)) {
          barcodes.set(barcode, []);
        }
        barcodes.get(barcode).push(file.name);
      }
    });
    return barcodes;
  }, []);

  const extractBrands = useCallback((files) => {
    const brands = [];
    let hasNew = false;
    let hasInstock = false;
    
    files.forEach(file => {
      const name = file.name.replace(/\.png$/i, '');
      if (name.toLowerCase() === 'new') {
        hasNew = true;
      } else if (name.toLowerCase() === 'instock') {
        instock = true;
      } else {
        brands.push(name);
      }
    });
    
    return { brands, hasNew, hasInstock };
  }, []);

  const handleSelectPhotoFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      setPhotoFolderHandle(dirHandle);

      const files = [];
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file' && entry.name.toLowerCase().endsWith('.jpg')) {
          files.push({ name: entry.name, handle: entry });
        }
      }

      const barcodeMap = extractBarcodes(files);
      setPreviewData(prev => ({ 
        ...prev, 
        barcodes: Array.from(barcodeMap.keys()),
        barcodeMap,
        photoFiles: files,
        photoCount: files.length,
        photoFolderName: dirHandle.name,
        photoFolderPath: dirHandle.name
      }));
      toast.success(`${files.length} fotoğraf bulundu (${barcodeMap.size} benzersiz barkod)`);
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handleSelectLogoFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      setLogoFolderHandle(dirHandle);

      const files = [];
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file' && entry.name.toLowerCase().endsWith('.png')) {
          files.push({ name: entry.name, handle: entry });
        }
      }

      const brands = [];
      let hasNew = false;
      let hasInstock = false;
      
      files.forEach(file => {
        const name = file.name.replace(/\.png$/i, '');
        if (name.toLowerCase() === 'new') {
          hasNew = true;
        } else if (name.toLowerCase() === 'instock') {
          hasInstock = true;
        } else {
          brands.push(name);
        }
      });

      setPreviewData(prev => ({ 
        ...prev, 
        brands, 
        hasNew, 
        hasInstock,
        logoFiles: files,
        logoCount: files.length,
        logoFolderName: dirHandle.name,
        logoFolderPath: dirHandle.name
      }));
      toast.success(`${files.length} logo bulundu`);
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handleSelectOutputFolder = async () => {
    if (!isSupported) {
      toast.error('Tarayıcınız klasör seçimini desteklemiyor');
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
      setOutputFolderHandle(dirHandle);
      setPreviewData(prev => ({ ...prev, outputFolderName: dirHandle.name }));
      toast.success(`Çıktı klasörü: ${dirHandle.name}`);
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Klasör seçilemedi');
      }
    }
  };

  const handleExcelSelect = async () => {
    try {
      const [fileHandle] = await window.showOpenFilePicker({
        types: [{
          description: 'Excel Files',
          accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] }
        }]
      });
      
      setExcelFileHandle(fileHandle);
      setPreviewData(prev => ({ ...prev, excelName: fileHandle.name }));
      toast.success('Excel dosyası seçildi');
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Dosya seçilemedi');
      }
    }
  };

  const handleProcess = async () => {
    if (!photoFolderHandle || !logoFolderHandle || !outputFolderHandle || !excelFileHandle) {
      toast.error('Lütfen tüm klasörleri ve Excel dosyasını seçin');
      return;
    }

    setProcessing(true);
    setResults(null);

    try {
      // Read Excel file
      const excelFile = await excelFileHandle.getFile();
      const arrayBuffer = await excelFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true, defval: null });

      if (jsonData.length < 2) {
        throw new Error('Excel dosyası boş veya sadece başlık içeriyor');
      }

      // Find column indices
      const headers = jsonData[0].map(h => String(h || '').toLowerCase());
      const barcodeIdx = headers.findIndex(h => h.includes('barcode'));
      const brandIdx = headers.findIndex(h => h.includes('brand'));
      const dateIdx = headers.findIndex(h => h === 'date' || h.includes('date'));

      if (barcodeIdx === -1 || brandIdx === -1) {
        throw new Error('Excel dosyasında BARCODE ve BRAND sütunları bulunamadı');
      }

      // Build photo and logo maps
      const photoFiles = previewData.photoFiles || [];
      const logoFiles = previewData.logoFiles || [];
      
      // Ana yol + klasör adı birleştir
      const basePathClean = basePath.trim().replace(/\/+$/, '');
      const photoFolderName = previewData.photoFolderName || '';
      const logoFolderName = previewData.logoFolderName || '';
      
      const cleanPhotoPath = basePathClean ? `${basePathClean}/${photoFolderName}` : photoFolderName;
      const cleanLogoPath = basePathClean ? `${basePathClean}/${logoFolderName}` : logoFolderName;
      
      const barcodePhotos = new Map();
      photoFiles.forEach(f => {
        const match = f.name.match(/(\d{13})/);
        if (match) {
          const barcode = match[1];
          if (!barcodePhotos.has(barcode)) {
            barcodePhotos.set(barcode, []);
          }
          barcodePhotos.get(barcode).push(f.name);
        }
      });

      // Logo map - case insensitive ve dosya adı ile
      const logoMap = new Map();
      let newLogoFile = null;
      let instockLogoFile = null;
      
      logoFiles.forEach(f => {
        const nameWithoutExt = f.name.replace(/\.png$/i, '');
        const nameLower = nameWithoutExt.toLowerCase();
        
        if (nameLower === 'new') {
          newLogoFile = f.name;
        } else if (nameLower === 'instock') {
          instockLogoFile = f.name;
        } else {
          // Hem orijinal isim hem lowercase versiyonunu sakla
          logoMap.set(nameLower, f.name);
          logoMap.set(nameWithoutExt, f.name);
        }
      });

      // Date comparison
      const today = new Date();
      const twoDaysAgo = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000);

      // Process rows - orijinal header'ları koru
      const originalHeaders = jsonData[0];
      const outputHeaders = [...originalHeaders, '@foto1', '@foto2', '@foto3', '@marka', '@new'];
      const outputRows = [outputHeaders];

      // Tarih sütunlarını tespit et
      const dateColumns = [];
      headers.forEach((h, idx) => {
        if (h.includes('date') || h.includes('tarih')) {
          dateColumns.push(idx);
        }
      });

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) continue;

        let barcode = row[barcodeIdx];
        if (barcode === undefined || barcode === null) continue;
        
        // Handle scientific notation
        if (typeof barcode === 'number') {
          barcode = String(Math.round(barcode));
        } else {
          barcode = String(barcode).trim();
        }

        if (!barcodePhotos.has(barcode)) continue;

        // Brand - orijinal değeri al
        const brand = String(row[brandIdx] || '').trim();
        const brandLower = brand.toLowerCase().replace(/\s/g, '');

        // Check date for new/instock determination
        let isNew = false;
        if (dateIdx !== -1 && row[dateIdx] !== undefined && row[dateIdx] !== null) {
          const dateVal = row[dateIdx];
          const parsedDate = excelDateToJSDate(dateVal);
          if (parsedDate) {
            isNew = parsedDate >= twoDaysAgo;
          }
        }

        // Satır verilerini kopyala - header uzunluğuna eşitle ve tarihleri formatla
        const headerLength = originalHeaders.length;
        const processedRow = [];
        for (let idx = 0; idx < headerLength; idx++) {
          let cell = row[idx];
          // Tarih sütunlarını formatla
          if (dateColumns.includes(idx) && typeof cell === 'number') {
            const date = excelDateToJSDate(cell);
            cell = date ? formatDate(date) : cell;
          }
          processedRow.push(cell !== undefined ? cell : null);
        }

        // Get photos (sorted by number in filename)
        const photos = barcodePhotos.get(barcode).sort((a, b) => {
          const numA = parseInt(a.match(/_\((\d+)\)/)?.[1] || '0');
          const numB = parseInt(b.match(/_\((\d+)\)/)?.[1] || '0');
          return numA - numB;
        });
        
        const foto1 = photos[0] ? `${cleanPhotoPath}/${photos[0]}` : '';
        const foto2 = photos[1] ? `${cleanPhotoPath}/${photos[1]}` : '';
        const foto3 = photos[2] ? `${cleanPhotoPath}/${photos[2]}` : '';

        // Get brand logo - daha esnek eşleştirme
        let markaLogo = '';
        
        // Önce tam eşleşme dene
        if (logoMap.has(brand)) {
          markaLogo = `${cleanLogoPath}/${logoMap.get(brand)}`;
        } 
        // Sonra lowercase dene
        else if (logoMap.has(brandLower)) {
          markaLogo = `${cleanLogoPath}/${logoMap.get(brandLower)}`;
        }
        // Partial match dene
        else {
          for (const [key, value] of logoMap.entries()) {
            if (key.toLowerCase() === brandLower || brandLower.includes(key.toLowerCase())) {
              markaLogo = `${cleanLogoPath}/${value}`;
              break;
            }
          }
        }

        // New or instock icon
        let newIcon = '';
        if (isNew && newLogoFile) {
          newIcon = `${cleanLogoPath}/${newLogoFile}`;
        } else if (instockLogoFile) {
          newIcon = `${cleanLogoPath}/${instockLogoFile}`;
        }

        outputRows.push([...processedRow, foto1, foto2, foto3, markaLogo, newIcon]);
      }

      if (outputRows.length <= 1) {
        throw new Error('Eşleşen veri bulunamadı. Fotoğraf barkodları Excel verisiyle eşleşmiyor.');
      }

      // Create output workbook for XLSX
      const outputWorkbook = XLSX.utils.book_new();
      const outputWorksheet = XLSX.utils.aoa_to_sheet(outputRows);
      XLSX.utils.book_append_sheet(outputWorkbook, outputWorksheet, 'Sonuc');
      
      // Generate XLSX binary
      const xlsxBuffer = XLSX.write(outputWorkbook, { bookType: 'xlsx', type: 'array' });

      // Generate TXT content (tab-separated)
      const txtContent = outputRows.map(row => 
        row.map(cell => cell === null || cell === undefined ? '' : String(cell)).join('\t')
      ).join('\n');

      // Write XLSX file to output folder
      const xlsxFileHandle = await outputFolderHandle.getFileHandle('sonuc.xlsx', { create: true });
      const xlsxWritable = await xlsxFileHandle.createWritable();
      await xlsxWritable.write(new Blob([xlsxBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      await xlsxWritable.close();

      // Write TXT file to output folder
      const txtFileHandle = await outputFolderHandle.getFileHandle('sonuc.txt', { create: true });
      const txtWritable = await txtFileHandle.createWritable();
      await txtWritable.write(txtContent);
      await txtWritable.close();

      setResults({
        success: true,
        matchedCount: outputRows.length - 1,
        filesWritten: ['sonuc.xlsx', 'sonuc.txt'],
        outputFolder: outputFolderHandle.name
      });

      toast.success(`İşlem tamamlandı! ${outputRows.length - 1} satır eşleştirildi.`);
      
    } catch (err) {
      console.error('Veri eşleştirme hatası:', err);
      setResults({
        success: false,
        error: err.message
      });
      toast.error(err.message || 'Veri eşleştirme başarısız');
    } finally {
      setProcessing(false);
    }
  };

  const handleClear = () => {
    setPhotoFolderHandle(null);
    setLogoFolderHandle(null);
    setOutputFolderHandle(null);
    setExcelFileHandle(null);
    setPreviewData(null);
    setResults(null);
    // Ana yolu temizleme - localStorage'da kalsın
    toast.info('Temizlendi');
  };

  const isReady = photoFolderHandle && logoFolderHandle && outputFolderHandle && excelFileHandle && basePath.trim();

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="data-match-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Veri Eşleştir
        </h1>
        <p className="text-gray-600 mt-2">
          Fotoğrafları, logoları ve Excel verilerini eşleştirerek InDesign için hazır çıktı alın
        </p>
      </div>

      {!isSupported && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <p>Tarayıcınız klasör erişimini desteklemiyor. Lütfen <strong>Chrome</strong> veya <strong>Edge</strong> kullanın.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Photo Folder */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Image className="w-4 h-4 text-purple-500" />
              1. Fotoğraflar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleSelectPhotoFolder}
              disabled={!isSupported || processing}
              variant="outline"
              className="w-full h-20 border-2 border-dashed border-purple-200 hover:border-purple-400 hover:bg-purple-50"
              data-testid="photo-folder-btn"
            >
              <div className="flex flex-col items-center gap-1">
                <FolderOpen className="w-6 h-6 text-purple-400" />
                <span className="text-xs">Klasör Seç</span>
              </div>
            </Button>
            {previewData?.photoFolderName && (
              <div className="mt-2 bg-purple-50 rounded p-2">
                <p className="text-xs font-medium text-purple-700 truncate">✓ {previewData.photoFolderName}</p>
                <p className="text-xs text-purple-600">{previewData.photoCount} fotoğraf</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logo Folder */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Palette className="w-4 h-4 text-pink-500" />
              2. Logolar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleSelectLogoFolder}
              disabled={!isSupported || processing}
              variant="outline"
              className="w-full h-20 border-2 border-dashed border-pink-200 hover:border-pink-400 hover:bg-pink-50"
              data-testid="logo-folder-btn"
            >
              <div className="flex flex-col items-center gap-1">
                <FolderOpen className="w-6 h-6 text-pink-400" />
                <span className="text-xs">Klasör Seç</span>
              </div>
            </Button>
            {previewData?.logoFolderName && (
              <div className="mt-2 bg-pink-50 rounded p-2">
                <p className="text-xs font-medium text-pink-700 truncate">✓ {previewData.logoFolderName}</p>
                <p className="text-xs text-pink-600">{previewData.logoCount} logo</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {previewData.hasNew && <span className="text-xs bg-green-100 text-green-700 px-1 rounded">new✓</span>}
                  {previewData.hasInstock && <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">instock✓</span>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Excel File */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="w-4 h-4 text-green-500" />
              3. Excel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleExcelSelect}
              disabled={!isSupported || processing}
              variant="outline"
              className="w-full h-20 border-2 border-dashed border-green-200 hover:border-green-400 hover:bg-green-50"
              data-testid="excel-btn"
            >
              <div className="flex flex-col items-center gap-1">
                <FileSpreadsheet className="w-6 h-6 text-green-400" />
                <span className="text-xs">Dosya Seç</span>
              </div>
            </Button>
            {previewData?.excelName && (
              <div className="mt-2 bg-green-50 rounded p-2">
                <p className="text-xs font-medium text-green-700 truncate">✓ {previewData.excelName}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Output Folder */}
        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FolderOutput className="w-4 h-4 text-blue-500" />
              4. Çıktı
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleSelectOutputFolder}
              disabled={!isSupported || processing}
              variant="outline"
              className="w-full h-20 border-2 border-dashed border-blue-200 hover:border-blue-400 hover:bg-blue-50"
              data-testid="output-folder-btn"
            >
              <div className="flex flex-col items-center gap-1">
                <FolderOpen className="w-6 h-6 text-blue-400" />
                <span className="text-xs">Klasör Seç</span>
              </div>
            </Button>
            {previewData?.outputFolderName && (
              <div className="mt-2 bg-blue-50 rounded p-2">
                <p className="text-xs font-medium text-blue-700 truncate">✓ {previewData.outputFolderName}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ana Klasör Yolu */}
      <Card className="mt-6 shadow-lg border-0 bg-white/80 backdrop-blur">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-gray-700">
            Ana Klasör Yolu (Tek Seferlik)
          </CardTitle>
          <CardDescription className="text-xs">
            Klasörlerin bulunduğu üst dizinin yolu - bir kez girin, sonra hep kalır
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="/Users/kullanici/Desktop"
              value={basePath}
              onChange={(e) => handleBasePathChange(e.target.value)}
              className={`text-sm flex-1 ${!basePath.trim() ? 'border-orange-300' : 'border-green-300'}`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text) {
                    handleBasePathChange(text.trim());
                    toast.success('Yol yapıştırıldı');
                  }
                } catch (e) {
                  toast.error('Panodan okunamadı');
                }
              }}
              className="px-3"
            >
              Yapıştır
            </Button>
          </div>
          
          {basePath && previewData?.photoFolderName && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-green-700 font-medium mb-1">Oluşacak yollar:</p>
              <p className="text-xs text-green-600 font-mono">📷 {basePath.replace(/\/+$/, '')}/{previewData.photoFolderName}/</p>
              {previewData?.logoFolderName && (
                <p className="text-xs text-green-600 font-mono">🎨 {basePath.replace(/\/+$/, '')}/{previewData.logoFolderName}/</p>
              )}
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            💡 Mac: Finder'da klasöre <strong>Option + sağ tık</strong> → "Copy as Pathname"
          </p>
        </CardContent>
      </Card>

      {/* Preview */}
      {previewData && (previewData.barcodes?.length > 0 || previewData.brands?.length > 0) && (
        <Card className="mt-6 shadow-lg border-0 bg-white/80 backdrop-blur">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FolderOpen className="w-5 h-5 text-blue-500" />
              Önizleme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              {previewData.barcodes?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Barkodlar ({previewData.barcodes.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {previewData.barcodes.slice(0, 6).map((barcode, idx) => (
                      <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-mono">
                        {barcode}
                      </span>
                    ))}
                    {previewData.barcodes.length > 6 && (
                      <span className="text-xs text-gray-500 py-1">+{previewData.barcodes.length - 6}</span>
                    )}
                  </div>
                </div>
              )}
              {previewData.brands?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Markalar ({previewData.brands.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {previewData.brands.slice(0, 6).map((brand, idx) => (
                      <span key={idx} className="text-xs bg-pink-100 text-pink-700 px-2 py-1 rounded">
                        {brand}
                      </span>
                    ))}
                    {previewData.brands.length > 6 && (
                      <span className="text-xs text-gray-500 py-1">+{previewData.brands.length - 6}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && (
        <Card className={`mt-6 shadow-lg border-0 ${results.success ? 'bg-green-50' : 'bg-red-50'}`}>
          <CardContent className="pt-6">
            {results.success ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">İşlem Başarılı!</span>
                </div>
                <p className="text-sm text-green-600">
                  {results.matchedCount} satır eşleştirildi ve "{results.outputFolder}" klasörüne kaydedildi.
                </p>
                <div className="flex gap-2 mt-2">
                  {results.filesWritten.map((file, idx) => (
                    <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {file}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <span>{results.error}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <Button
          onClick={handleProcess}
          disabled={!isReady || processing}
          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
          data-testid="process-button"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              İşleniyor...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Eşleştir ve Kaydet
            </>
          )}
        </Button>

        <Button
          variant="outline"
          onClick={handleClear}
          disabled={processing}
          className="hover:bg-red-50 hover:text-red-600 hover:border-red-300"
          data-testid="clear-button"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Temizle
        </Button>
      </div>

      {/* Help */}
      <Card className="mt-6 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-purple-50">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-gray-800 mb-3">Nasıl Çalışır?</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>1. <strong>Fotoğraf Klasörü:</strong> Barkod içeren JPG dosyalarının bulunduğu klasörü seçin</p>
            <p>2. <strong>Logo Klasörü:</strong> Marka logoları + new.png, instock.png dosyalarının bulunduğu klasörü seçin</p>
            <p>3. <strong>Excel Dosyası:</strong> BARCODE, BRAND, DATE sütunlarını içeren Excel dosyasını seçin</p>
            <p>4. <strong>Çıktı Klasörü:</strong> Sonuç dosyalarının kaydedileceği klasörü seçin</p>
          </div>
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Çıktı:</strong> sonuc.xlsx ve sonuc.txt (InDesign için tab-separated) dosyaları oluşturulur.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// Wrapper with FeatureGuard
export default function DataMatch() {
  return (
    <FeatureGuard featureKey="data_match" pageName="Veri Eşleştir">
      <DataMatchContent />
    </FeatureGuard>
  );
}
